/**
 * Zustand Store for Checkout State
 */

import { create } from 'zustand';
import type { CheckoutSession, Plan } from '../types/api';
import checkoutApi from '../lib/api';

export interface CheckoutState {
  // Session state
  session: CheckoutSession | null;
  sessionLoading: boolean;
  sessionError: string | null;
  organizationId: string | null;

  // Plans state
  plans: Plan[];
  plansLoading: boolean;
  plansError: string | null;

  // UI state
  isSubmitting: boolean;
  successMessage: string | null;
  errorMessage: string | null;

  // Actions
  loadSession: (sessionId: string) => Promise<void>;
  clearSession: () => void;
  loadPlans: () => Promise<void>;
  setOrganizationId: (id: string | null) => void;
  setSubmitting: (submitting: boolean) => void;
  setSuccessMessage: (message: string | null) => void;
  setErrorMessage: (message: string | null) => void;
  clearMessages: () => void;
}

export const useCheckoutStore = create<CheckoutState>((set) => ({
  // Initial state
  session: null,
  sessionLoading: false,
  sessionError: null,
  organizationId: null,
  plans: [],
  plansLoading: false,
  plansError: null,
  isSubmitting: false,
  successMessage: null,
  errorMessage: null,

  // Actions
  loadSession: async (sessionId: string) => {
    set({ sessionLoading: true, sessionError: null });
    try {
      // Get session using only session_id (no authentication required)
      const session = await checkoutApi.getSession(sessionId);

      // Extract organization_id from session response
      const orgId = session.organization_id;
      if (!orgId) {
        throw new Error('Session does not contain organization_id');
      }

      set({ session, sessionLoading: false, organizationId: orgId });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load session';
      set({ sessionError: message, sessionLoading: false });
      throw error;
    }
  },

  clearSession: () => {
    set({ session: null, sessionError: null, organizationId: null });
  },

  setOrganizationId: (id: string | null) => {
    set({ organizationId: id });
  },

  loadPlans: async () => {
    set({ plansLoading: true, plansError: null });
    try {
      const plans = await checkoutApi.getPlans();
      set({ plans, plansLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load plans';
      set({ plansError: message, plansLoading: false });
      throw error;
    }
  },

  setSubmitting: (submitting: boolean) => {
    set({ isSubmitting: submitting });
  },

  setSuccessMessage: (message: string | null) => {
    set({ successMessage: message });
  },

  setErrorMessage: (message: string | null) => {
    set({ errorMessage: message });
  },

  clearMessages: () => {
    set({ successMessage: null, errorMessage: null });
  },
}));
